import { Quote, QuoteLineItem, PlanOption } from '../types/calculator';
import { licensePlans } from '../data/licensePlans';
import { PricingPeriod } from '../types/pricing';
import { getPriceForPeriod } from './pricingUtils';
import { LicensePlan } from '../types/license';

interface ParsedPriceOption {
  suffix: string;
  priceLabel: string;
  price?: number;
  quotable: boolean;
}

const STUDENT_FACULTY_PATTERN =
  /^\$([\d.]+)\s*user\/month\s*\(students\)\s*\/\s*\$([\d.]+)\s*user\/month\s*\(faculty\)$/i;

const SINGLE_PRICE_PATTERN = /^\$([\d.]+)\s*user\/month$/i;

export const parseCalculatorPriceOptions = (priceString: string): ParsedPriceOption[] => {
  const trimmed = priceString.trim();

  if (/^free$/i.test(trimmed)) {
    return [{ suffix: '', priceLabel: 'Free', quotable: false }];
  }

  if (/^contact sales$/i.test(trimmed)) {
    return [{ suffix: '', priceLabel: 'Contact Sales', quotable: false }];
  }

  const studentFaculty = trimmed.match(STUDENT_FACULTY_PATTERN);
  if (studentFaculty) {
    const studentAmount = parseFloat(studentFaculty[1]);
    const facultyAmount = parseFloat(studentFaculty[2]);
    return [
      {
        suffix: ' (Students)',
        priceLabel: `$${studentAmount.toFixed(2)} user/month (students)`,
        price: studentAmount,
        quotable: true,
      },
      {
        suffix: ' (Faculty)',
        priceLabel: `$${facultyAmount.toFixed(2)} user/month (faculty)`,
        price: facultyAmount,
        quotable: true,
      },
    ];
  }

  const single = trimmed.match(SINGLE_PRICE_PATTERN);
  if (single) {
    const amount = parseFloat(single[1]);
    return [{ suffix: '', priceLabel: trimmed, price: amount, quotable: true }];
  }

  return [{ suffix: '', priceLabel: trimmed, quotable: false }];
};

export const planOptionsFromLicensePlan = (plan: LicensePlan, period: PricingPeriod): PlanOption[] => {
  const priceString = getPriceForPeriod(plan, period);
  const category = getCategoryFromId(plan.id);

  return parseCalculatorPriceOptions(priceString).map((parsed) => ({
    id: parsed.suffix ? `${plan.id}-${parsed.suffix.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}` : plan.id,
    name: `${plan.name}${parsed.suffix}`,
    category,
    priceLabel: parsed.priceLabel,
    price: parsed.price,
    quotable: parsed.quotable,
    description: plan.description,
  }));
};

export const getAllPlanOptions = (period: PricingPeriod = 'atRenewal'): PlanOption[] => {
  return licensePlans.flatMap(plan => planOptionsFromLicensePlan(plan, period));
};

const getCategoryFromId = (id: string): string => {
  if (id.startsWith('m365-business')) return 'Business';
  if (id.startsWith('m365-e')) return 'Enterprise';
  if (id.startsWith('m365-f')) return 'Frontline';
  if (id.startsWith('m365-a')) return 'Education';
  if (id.startsWith('m365-gov') || id.startsWith('gcc')) return 'Government';
  if (id.startsWith('office365') || id.startsWith('o365')) return 'Office 365';
  return 'Other';
};

export const formatPlanOptionLabel = (plan: PlanOption): string => {
  if (plan.quotable && plan.price !== undefined) {
    return `${plan.name} - ${formatCurrency(plan.price)}/user/month`;
  }
  return `${plan.name} - ${plan.priceLabel}`;
};

export const calculateLineItemSubtotal = (pricePerUser: number, quantity: number): number => {
  return pricePerUser * quantity;
};

export const calculateQuoteTotal = (items: QuoteLineItem[]): number => {
  return items.reduce((total, item) => (item.quotable ? total + item.subtotal : total), 0);
};

export const calculateAnnualTotal = (monthlyTotal: number): number => {
  return monthlyTotal * 12;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export const generateQuoteId = (): string => {
  return `quote-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const saveQuoteToLocalStorage = (quote: Quote): void => {
  const quotes = getQuotesFromLocalStorage();
  const existingIndex = quotes.findIndex(q => q.id === quote.id);

  if (existingIndex >= 0) {
    quotes[existingIndex] = quote;
  } else {
    quotes.push(quote);
  }

  localStorage.setItem('m365-quotes', JSON.stringify(quotes));
};

export const getQuotesFromLocalStorage = (): Quote[] => {
  const quotesJson = localStorage.getItem('m365-quotes');
  return quotesJson ? JSON.parse(quotesJson) : [];
};

export const deleteQuoteFromLocalStorage = (quoteId: string): void => {
  const quotes = getQuotesFromLocalStorage();
  const filtered = quotes.filter(q => q.id !== quoteId);
  localStorage.setItem('m365-quotes', JSON.stringify(filtered));
};

export const exportQuoteToText = (quote: Quote): string => {
  const lines = [
    '='.repeat(60),
    'MICROSOFT 365 LICENSE QUOTE',
    '='.repeat(60),
    '',
    `Quote Name: ${quote.name}`,
    `Date: ${new Date(quote.createdAt).toLocaleDateString()}`,
    `Billing Cycle: ${quote.billingCycle === 'monthly' ? 'Monthly' : 'Annual'}`,
    '',
    '-'.repeat(60),
    'LINE ITEMS',
    '-'.repeat(60),
    ''
  ];

  quote.items.forEach((item, index) => {
    lines.push(`${index + 1}. ${item.planName}`);
    lines.push(`   Quantity: ${item.quantity} users`);
    lines.push(`   Price per user: ${item.priceLabel}`);
    if (item.quotable && item.pricePerUser !== undefined) {
      lines.push(`   Subtotal: ${formatCurrency(item.subtotal)}/month`);
    } else {
      lines.push('   Subtotal: Not included in total');
    }
    lines.push('');
  });

  lines.push('-'.repeat(60));
  lines.push(`Monthly Total: ${formatCurrency(quote.total)}`);
  lines.push(`Annual Total: ${formatCurrency(calculateAnnualTotal(quote.total))}`);
  lines.push('='.repeat(60));
  lines.push('');
  lines.push('Created by Celebration OnPoint');
  lines.push('https://www.onpointhq.com');
  lines.push('');
  lines.push('Disclaimer: This quote is for estimation purposes only.');
  lines.push('Final pricing should be confirmed with a Microsoft Customer Price Sheet (CPS).');

  return lines.join('\n');
};
